import { useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type CalendarAccount,
  type CalendarAccountsResponse,
  type CalendarEvent,
} from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { currentBrand } from '../lib/brand.js';

export function CalendarPage(): JSX.Element {
  const [info, setInfo] = useState<CalendarAccountsResponse | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    try {
      const [accounts, evs] = await Promise.all([api.calendarAccounts(), api.calendarEvents()]);
      setInfo(accounts);
      setEvents(evs);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      if (err instanceof ApiError && err.status === 401) return;
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function toggleAutoJoin(a: CalendarAccount): Promise<void> {
    await api.setAutoJoin(a.id, !a.autoJoin);
    await load();
  }

  async function disconnect(a: CalendarAccount): Promise<void> {
    if (!confirm(`Disconnect ${a.email ?? a.provider}?`)) return;
    await api.disconnectAccount(a.id);
    await load();
  }

  async function sync(): Promise<void> {
    setBusy(true);
    try {
      await api.syncCalendar();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="page-title">Calendar</h1>
      <p className="page-subtitle">
        Connect Google or Microsoft so {currentBrand().name} can join meetings on your calendar.
      </p>

      {error && <div className="form-error" style={{ marginBottom: 14 }}>{error}</div>}

      {info && !info.encryptionReady && (
        <div className="form-error" style={{ marginBottom: 14 }}>
          Set <code>ENCRYPTION_KEY</code> on the API to store OAuth tokens encrypted at rest.
        </div>
      )}

      <div className="panel" style={{ marginBottom: 14 }}>
        <h3 className="panel__title">Connected calendars</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {info?.googleConfigured && (
            <a className="btn" href="/api/oauth/google/start">Connect Google Calendar</a>
          )}
          {info?.microsoftConfigured && (
            <a className="btn btn--ghost" href="/api/oauth/microsoft/start">Connect Microsoft</a>
          )}
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void sync()}>
            {busy ? 'Syncing…' : 'Sync now'}
          </button>
        </div>

        {info?.accounts.length === 0 && (
          <p className="panel__subtle">No calendars connected yet.</p>
        )}

        {info?.accounts.map((a) => (
          <div key={a.id} className="settings-row">
            <div>
              <div style={{ fontWeight: 600 }}>
                {a.provider === 'google_calendar' ? 'Google' : 'Microsoft'} · {a.email ?? 'unknown'}
              </div>
              <div className="panel__subtle">{a.name}</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={a.autoJoin}
                onChange={() => void toggleAutoJoin(a)}
              />
              Auto-join
            </label>
            <button type="button" className="btn btn--ghost" onClick={() => void disconnect(a)}>
              Disconnect
            </button>
          </div>
        ))}

      </div>

      <div className="panel">
        <h3 className="panel__title">Upcoming auto-join events</h3>
        {events.length === 0 && (
          <p className="panel__subtle">No upcoming events with meeting links.</p>
        )}
        <div className="library-list">
          {events.map((e) => (
            <div key={e.id} className="library-row" style={{ gridTemplateColumns: '1fr auto auto' }}>
              <div>
                <div className="library-row__title">{e.title ?? 'Untitled event'}</div>
                <div className="library-row__meta">
                  {formatDate(e.startAt)} · {e.provider.replace('_calendar', '')}
                  {e.meetingUrl ? ' · has meeting link' : ' · no link'}
                </div>
              </div>
              <span className={`status-badge status-badge--${e.joinStatus === 'scheduled' ? 'complete' : e.joinStatus === 'failed' ? 'failed' : 'idle'}`}>
                {e.joinStatus}
              </span>
              {e.meetingId && (
                <a href={`/meetings/${e.meetingId}`} className="library-row__cta">Meeting →</a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
